import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  #state = {
    activeItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener(
      'pointerleave',
      () => this.#debouncedDeactivate(),
      { signal: this.#abortController.signal }
    );

    onDocumentReady(() => {
      this.#preloadImages();
      this.#initialiseMegaMenus();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]') ?? null;
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  activate = (event) => {
    const menuItem = getMenuItemFromEvent(event);
    if (!menuItem) return;

    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event);
    } else {
      this.#longDebouncedActivateHandler(event);
    }
  };

  #activateHandler = (event) => {
    const menuItem = getMenuItemFromEvent(event);
    if (!menuItem) return;

    this.#debouncedDeactivate.cancel();
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    const parentLinkValue = menuItem.getAttribute('data-parent-link');
    if (!parentLinkValue) return;

    const isDefaultSlot = menuItem.slot === '';
    const isMoreSlot = menuItem.slot === 'more';
    const submenuWrapper = findSubmenuWrapper(menuItem);

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    // Deactivate previously active item
    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem && previouslyActiveItem !== menuItem) {
      const prevAnchor = findMenuAnchor(previouslyActiveItem);
      if (prevAnchor) prevAnchor.ariaExpanded = 'false';
      previouslyActiveItem.classList.remove('active');
      // Hide any desktop mega menu block from the previous item
      const previousWrapper = findSubmenuWrapper(previouslyActiveItem);
      if (previousWrapper instanceof HTMLElement) {
        this.#hideAllMegaMenuBlocks(previousWrapper);
      }
    }

    // If no submenu exists and not overflow, close
    if (!(submenuWrapper instanceof HTMLElement) && !isMoreSlot) {
      this.style.setProperty('--submenu-height', '0px');
      this.dataset.overflowExpanded = 'false';
      this.#state.activeItem = null;
      this.ariaExpanded = 'false';
      const closingAnchor = findMenuAnchor(menuItem);
      if (closingAnchor) closingAnchor.ariaExpanded = 'false';
      menuItem.classList.remove('active');
      return;
    }

    this.#state.activeItem = menuItem;
    this.ariaExpanded = 'true';
    // Set aria-expanded on the <a role="menuitem"> — this is what the CSS :has() selector targets
    const menuAnchor = findMenuAnchor(menuItem);
    if (menuAnchor) menuAnchor.ariaExpanded = 'true';
    menuItem.classList.add('active');

    let contentHeight = 0;

    if (isMoreSlot) {
      contentHeight = this.overflowMenu?.offsetHeight ?? 0;
      this.style.setProperty('--submenu-height', `${contentHeight}px`);
    } else if (submenuWrapper instanceof HTMLElement) {
      // Check if there's a matching Desktop Mega Menu block for this nav item.
      // If not, don't open the submenu at all.
      const megaBlock = submenuWrapper.querySelector(`.mega-menu[data-parent-link="${parentLinkValue}"]`);

      if (!(megaBlock instanceof HTMLElement)) {
        // No mega menu block configured for this item — close and bail out
        this.style.setProperty('--submenu-height', '0px');
        this.ariaExpanded = 'false';
        if (menuAnchor) menuAnchor.ariaExpanded = 'false';
        menuItem.classList.remove('active');
        this.#state.activeItem = null;
        return;
      }

      // Show the matching Desktop Mega Menu block FIRST.
      // ariaExpanded is now set on the <a> above, which triggers CSS :has([aria-expanded='true'])
      // making the submenu visible — so our height measurement will be accurate.
      this.#showMatchingMegaMenu(submenuWrapper, parentLinkValue);

      // Measure AFTER a rAF so the CSS visibility change has applied and layout is complete.
      requestAnimationFrame(() => {
        // getBoundingClientRect gives the rendered height (accurate after CSS applies)
        const rect = submenuWrapper.getBoundingClientRect();
        contentHeight = rect.height > 0 ? rect.height : submenuWrapper.scrollHeight;

        // Fallback if submenu still reports 0 — measure the mega menu block directly
        if (contentHeight < 50) {
          contentHeight = megaBlock.scrollHeight;
        }

        this.style.setProperty('--submenu-height', `${contentHeight}px`);
      });
    }
  };

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    const menuItem = getMenuItemFromEvent(event);
    if (!menuItem) return;

    if (menuItem === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  #deactivate = (menuItem = this.#state.activeItem) => {
    if (!menuItem || menuItem !== this.#state.activeItem) return;
    if (this.overflowHovered) return;

    const submenuWrapper = findSubmenuWrapper(menuItem);

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    const deactivatingAnchor = findMenuAnchor(menuItem);
    if (deactivatingAnchor) deactivatingAnchor.ariaExpanded = 'false';
    menuItem.classList.remove('active');
    menuItem.setAttribute('data-animating', '');

    if (submenuWrapper instanceof HTMLElement) {
      this.#hideAllMegaMenuBlocks(submenuWrapper);
    }

    setTimeout(() => {
      menuItem.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * On init, hide all Desktop Mega Menu blocks using a CSS class.
   * The standard submenu content is unaffected — shown via CSS :has([aria-expanded='true']).
   */
  #initialiseMegaMenus = () => {
    document.querySelectorAll('.mega-menu[data-parent-link]').forEach((menu) => {
      if (menu instanceof HTMLElement) {
        menu.classList.remove('mega-menu--visible');
      }
    });
  };

  /**
   * Hide all Desktop Mega Menu blocks inside a submenu wrapper.
   */
  #hideAllMegaMenuBlocks = (submenuWrapper) => {
    submenuWrapper.querySelectorAll('.mega-menu[data-parent-link]').forEach((menu) => {
      menu.classList.remove('mega-menu--visible');
    });
  };

  /**
   * Show the Desktop Mega Menu block matching parentLinkValue.
   * If no match, standard submenu content shows via CSS.
   */
  #showMatchingMegaMenu = (submenuWrapper, parentLinkValue) => {
    this.#hideAllMegaMenuBlocks(submenuWrapper);

    const targetMenu = submenuWrapper.querySelector(
      `.mega-menu[data-parent-link="${parentLinkValue}"]`
    );

    if (targetMenu instanceof HTMLElement) {
      targetMenu.classList.add('mega-menu--visible');
    }
  };

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function getMenuItemFromEvent(event) {
  if (!(event instanceof Event)) return null;

  // We want the <li data-parent-link> element for submenu/mega-menu lookup
  const current = event.currentTarget;
  if (current instanceof HTMLElement && current.matches('.menu-list__list-item[data-parent-link]')) {
    return current;
  }

  const target = event.target;
  if (target instanceof Element) {
    return target.closest('.menu-list__list-item[data-parent-link]');
  }

  return null;
}

/**
 * Find the <a role="menuitem"> inside a list item.
 * The CSS uses :has([aria-expanded='true']) so aria-expanded must be on this element.
 */
function findMenuAnchor(listItem) {
  if (!(listItem instanceof HTMLElement)) return null;
  return listItem.querySelector('[role="menuitem"]');
}

function findSubmenuWrapper(menuItem) {
  if (!(menuItem instanceof HTMLElement)) return null;
  return menuItem.querySelector('.menu-list__submenu');
}